import React, {useState} from 'react';
import {downloadCSV, ExportButton, List, useRedirect, useListContext, usePermissions, useRefresh, useTranslate} from 'react-admin';
import jsonExport from 'jsonexport/dist';
import {Avatar, Box, Pagination, Typography} from '@mui/material';
import {AddRounded} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SavedViewsTabs from '../components/statamic/SavedViewsTabs';
import FilterBar, {FilterDropdown} from '../components/statamic/FilterBar';
import ScopeBanner from '../components/statamic/ScopeBanner';
import useScopedFilter from '../scope/useScopedFilter';
import StatamicTable from '../components/statamic/StatamicTable';
import StatusDot from '../components/statamic/StatusDot';
import StatusPill from '../components/statamic/StatusPill';
import DeleteConfirmDialog from '../components/statamic/DeleteConfirmDialog';
import hasPermission from '../auth/hasPermission';

const childExporter = (children) => {
    const rows = children.map((child) => {
        const {
            backlinks, profile_image_thumbnail, profile_video_thumbnail, nr_of_guardians, _links,
            country, location, description, family, help, profile_video, video_ext,
            guardians, translations, ...rest
        } = child;
        return rest;
    });
    jsonExport(rows, {headers: ['id', 'first_name', 'last_name']}, (err, csv) => {
        downloadCSV(csv, 'kinderen');
    });
};

const formatDate = (v) => {
    if (!v) return null;
    const d = new Date(v); return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
};

const ageFromDate = (v) => {
    if (!v) return null;
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    const ms = Date.now() - d.getTime();
    const years = Math.floor(ms / (1000 * 60 * 60 * 24 * 365.25));
    return years;
};

const ChildCell = ({record, translate}) => {
    const photoUrl = record.profile_image_thumbnail
        ? `${process.env.REACT_APP_CDN_URL}/${record.profile_image_thumbnail}`
        : undefined;

    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
            <Avatar
                src={photoUrl}
                sx={{
                    width: 36,
                    height: 36,
                    bgcolor: t.color.sidebarHover,
                    color: t.color.text.secondary,
                    border: `1px solid ${t.color.border.hairline}`,
                    fontSize: 13,
                }}
            >
                {(record.first_name?.charAt(0) ?? '') + (record.last_name?.charAt(0) ?? '')}
            </Avatar>
            <Box sx={{minWidth: 0}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, minWidth: 0}}>
                    <Typography
                        sx={{
                            fontSize: `${t.type.bodyStrong.size}px`,
                            fontWeight: t.type.bodyStrong.weight,
                            color: t.color.text.primary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {[record.first_name, record.last_name].filter(Boolean).join(' ') || translate('Unnamed')}
                    </Typography>
                    {/* A project-level record holds project updates, not a child; the dashboard does not count it. */}
                    {record.project_level === true && (
                        <StatusPill variant="neutral">{translate('Project level')}</StatusPill>
                    )}
                </Box>
                {record.birth_date && (
                    <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary}}>
                        {translate('Age %{years}', {years: ageFromDate(record.birth_date) ?? '–'})}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

const BooleanCell = ({value, translate}) => (
    <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.75}}>
        <StatusDot variant={value ? 'success' : 'neutral'}/>
        <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.primary}}>
            {value ? translate('Yes') : translate('No')}
        </Typography>
    </Box>
);

const StatamicPagination = () => {
    const {page, perPage, total, setPage} = useListContext();
    const translate = useTranslate();
    const pageCount = Math.max(1, Math.ceil((total ?? 0) / (perPage ?? 25)));
    if (pageCount <= 1) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 1.5,
                borderTop: `1px solid ${t.color.border.hairline}`,
            }}
        >
            <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.muted}}>
                {translate('Showing page %{page} of %{pageCount} · %{total} total', {page, pageCount, total: total ?? 0})}
            </Typography>
            <Pagination
                count={pageCount}
                page={page}
                onChange={(_, p) => setPage(p)}
                size="small"
                shape="rounded"
                sx={{
                    '& .MuiPaginationItem-root': {
                        fontSize: `${t.type.caption.size}px`,
                        color: t.color.text.secondary,
                        borderColor: t.color.border.default,
                    },
                    '& .Mui-selected': {
                        backgroundColor: `${t.color.accent.primaryTint} !important`,
                        color: `${t.color.accent.primary} !important`,
                    },
                }}
            />
        </Box>
    );
};

const SurfaceCard = ({children}) => (
    <Box
        sx={{
            backgroundColor: t.color.surface,
            border: `1px solid ${t.color.border.default}`,
            borderRadius: `${t.radius.md}px`,
            mx: 3,
            mb: 3,
            overflow: 'hidden',
        }}
    >
        {children}
    </Box>
);

const ChildListStatamicInner = () => {
    const redirect = useRedirect();
    const refresh = useRefresh();
    const translate = useTranslate();
    const {filterValues, setFilters, displayedFilters} = useListContext();
    const {permissions} = usePermissions();
    const [activeViewId, setActiveViewId] = useState('all');
    const [deleteRecord, setDeleteRecord] = useState(null);
    useScopedFilter('v2Children');

    const canDelete = hasPermission(permissions, ['delete'], 'children.delete');

    const views = [
        {id: 'all',          label: translate('All')},
        {id: 'verified',     label: translate('Verified')},
        {id: 'sponsored',    label: translate('Sponsored')},
        {id: 'unsponsored',  label: translate('Unsponsored')},
    ];

    const handleViewSelect = (id) => {
        setActiveViewId(id);
        const base = {...filterValues};
        delete base.verified;
        delete base.child_is_sponsored;
        if (id === 'verified')          setFilters({...base, verified: true}, displayedFilters);
        else if (id === 'sponsored')    setFilters({...base, child_is_sponsored: true}, displayedFilters);
        else if (id === 'unsponsored')  setFilters({...base, child_is_sponsored: false}, displayedFilters);
        else                            setFilters(base, displayedFilters);
    };

    const rowActions = (record) => [
        {label: translate('View'),   onClick: () => redirect('show', 'children', record.id)},
        {label: translate('Edit'),   onClick: () => redirect('edit', 'children', record.id)},
        canDelete && {label: translate('Delete'), variant: 'danger', onClick: () => setDeleteRecord(record)},
    ].filter(Boolean);

    const columns = [
        { key: 'child', label: translate('Child'), sortable: true, sortField: 'first_name', render: (r) => <ChildCell record={r} translate={translate}/> },
        { key: 'verified', label: translate('Verified'), width: 110, render: (r) => <BooleanCell value={r.verified} translate={translate}/> },
        { key: 'sponsored', label: translate('Sponsored'), width: 130, render: (r) =>
            r.child_is_sponsored
                ? <StatusPill variant="success">{translate('Sponsored')}</StatusPill>
                : <StatusPill variant="neutral">{translate('Unsponsored')}</StatusPill>
        },
        { key: 'charity', label: translate('Charity'), render: (r) => (
            <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.secondary}}>
                {r.charity_name || '—'}
            </Typography>
        )},
        { key: 'birthdate', label: translate('Birth date'), sortable: true, sortField: 'birth_date', width: 130, render: (r) => (
            <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.secondary}}>
                {formatDate(r.birth_date) ?? '—'}
            </Typography>
        )},
    ];

    const updateFilter = (src, v) => setFilters({...filterValues, [src]: v}, displayedFilters);
    const clearFilter = (src) => {
        const next = {...filterValues};
        delete next[src];
        setFilters(next, displayedFilters);
    };

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <PageHeader
                title={translate('Children')}
                subtitle={translate('All child profiles across umbrellas and charities')}
                actions={
                    <>
                        <ExportButton label={translate('Export')}/>
                        <PrimaryButton
                            variant="primary"
                            startIcon={<AddRounded sx={{fontSize: 18}}/>}
                            onClick={() => redirect('create', 'children')}
                        >
                            {translate('Create child')}
                        </PrimaryButton>
                    </>
                }
            />

            <SurfaceCard>
                <ScopeBanner/>
                <SavedViewsTabs
                    views={views}
                    activeId={activeViewId}
                    onSelect={handleViewSelect}
                    onAdd={() => {}}
                />

                <FilterBar
                    searchValue={filterValues?.q}
                    onSearchChange={(q) => setFilters({...filterValues, q}, displayedFilters)}
                    placeholder={translate('Search children')}
                    filters={
                        <>
                            <FilterDropdown
                                label={translate('Verified')}
                                source="verified"
                                type="boolean"
                                value={filterValues?.verified}
                                onChange={updateFilter}
                                onClear={clearFilter}
                            />
                            <FilterDropdown
                                label={translate('Sponsored')}
                                source="child_is_sponsored"
                                type="boolean"
                                value={filterValues?.child_is_sponsored}
                                onChange={updateFilter}
                                onClear={clearFilter}
                            />
                        </>
                    }
                />

                <StatamicTable
                    columns={columns}
                    rowActions={rowActions}
                    onRowClick={(record) => redirect('show', 'children', record.id)}
                />

                <StatamicPagination/>
            </SurfaceCard>

            {deleteRecord && (
                <DeleteConfirmDialog
                    open
                    onClose={() => setDeleteRecord(null)}
                    resource="children"
                    record={deleteRecord}
                    recordLabel={[deleteRecord.first_name, deleteRecord.last_name].filter(Boolean).join(' ') || undefined}
                    onSuccess={refresh}
                />
            )}
        </Box>
    );
};

const ChildListStatamic = (props) => (
    <List
        {...props}
        sort={{field: 'create_date_time', order: 'DESC'}}
        perPage={25}
        actions={false}
        exporter={childExporter}
        pagination={false}
        component={Box}
        sx={{
            backgroundColor: t.color.page,
            '& .RaList-main': {backgroundColor: t.color.page},
        }}
    >
        <ChildListStatamicInner/>
    </List>
);

export default ChildListStatamic;
